// The app owner's own build and "Submit for publishing". A client sees its
// uploads (or the builds App Factory made for it) and where the submission
// stands; our staff see the same panel with the controls to move a submission
// along (the publisher is still picked in the Share panel below it).
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_AAB_BYTES, cancelSubmission, downloadBuildApk, getSdkOffer, getSubmissions, getUploadedBuilds, setSdkOffer,
  submitForPublishing, updateSubmission, uploadAab, type Submission, type SubmissionStatus, type UploadedBuild,
} from '@/api/client-publish';
import { getCampaigns } from '@/api/tracker-campaigns';
import { Button } from '@/components/Button';
import { saveBlob } from '@/lib/icon-export';
import { useAuthStore } from '@/store/auth';

const STATUS_LABEL: Record<SubmissionStatus, string> = {
  submitted: 'Submitted — waiting for review',
  in_review: 'In review',
  published: 'Published',
  rejected: 'Needs changes',
  cancelled: 'Withdrawn',
};

function stamp(seconds: number): string {
  return new Date(seconds * 1000).toISOString().replace('T', ' ').slice(0, 16);
}

function megabytes(bytes: number | null): string {
  return bytes == null ? '' : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function PublishPanel({ appId }: { appId: string }) {
  const qc = useQueryClient();
  const isClient = useAuthStore((s) => s.isClient());
  const canEdit = useAuthStore((s) => s.canEdit());
  // Moving a submission along is for technical admins, like pinning a build.
  const isTechnical = useAuthStore((s) => s.admin?.role === 'super_admin' || !!s.admin?.addons?.includes('developer'));
  const buildsQ = useQuery({ queryKey: ['client-builds', appId], queryFn: () => getUploadedBuilds(appId) });
  const subsQ = useQuery({ queryKey: ['submissions', appId], queryFn: () => getSubmissions(appId) });

  const fileRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [buildId, setBuildId] = useState('');
  const [comment, setComment] = useState('');
  const [downloading, setDownloading] = useState('');
  const [downloadError, setDownloadError] = useState('');

  const builds = buildsQ.data ?? [];
  // Only a bundle goes to the store; an APK is for installing and testing.
  const bundles = builds.filter((b) => b.build_type === 'aab');
  // App Factory signs the app: its new versions come from there, never an upload.
  const factoryBuilt = builds.some((b) => b.source === 'factory');
  const submissions = subsQ.data?.submissions ?? [];
  const missing = subsQ.data?.missing ?? [];
  const open = submissions.find((s) => s.status === 'submitted' || s.status === 'in_review');
  const chosenBuild = (bundles.some((b) => b.id === buildId) ? buildId : '') || bundles[0]?.id || '';

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['client-builds', appId] });
    void qc.invalidateQueries({ queryKey: ['submissions', appId] });
    void qc.invalidateQueries({ queryKey: ['apps'] });
  };

  async function onFile(file: File | undefined) {
    if (!file) return;
    setUploadError('');
    if (file.size > MAX_AAB_BYTES) {
      setUploadError('The bundle is larger than 100 MB');
      return;
    }
    setProgress(0);
    try {
      const build = await uploadAab(appId, file, setProgress);
      setBuildId(build.id);
      refresh();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const submit = useMutation({
    mutationFn: () => submitForPublishing(appId, chosenBuild, comment.trim() || undefined),
    onSuccess: () => { setComment(''); refresh(); },
  });
  const withdraw = useMutation({ mutationFn: (id: string) => cancelSubmission(id), onSuccess: refresh });

  async function downloadApk(b: UploadedBuild) {
    setDownloadError('');
    setDownloading(b.id);
    try {
      const blob = await downloadBuildApk(appId, b.id);
      saveBlob(blob, `${b.version_name || b.version_code || 'build'}.apk`.replace(/[^A-Za-z0-9._-]/g, '_'));
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading('');
    }
  }

  // Our own apps never use this flow (their builds from the Build tab show
  // here too); keep the Share tab as it was for them.
  if (!isClient && !builds.some((b) => b.source === 'upload') && submissions.length === 0) return null;

  return (
    <section className="bg-surface border border-border rounded-lg p-4 space-y-4">
      <div>
        <h3 className="font-medium text-content">Build &amp; publishing</h3>
        <p className="mt-1 text-sm text-content-subtle">
          {factoryBuilt
            ? 'App Factory builds and signs this app. Install the APK to test it, then submit the AAB for publishing.'
            : 'Upload the release AAB signed with your upload key. Every new upload needs a higher versionCode and the same key.'}
        </p>
      </div>

      {/* Builds */}
      <div className="space-y-2">
        {canEdit && !factoryBuilt && (
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".aab" className="hidden" onChange={(e) => void onFile(e.target.files?.[0])} />
            <Button variant="secondary" size="sm" disabled={progress !== null} onClick={() => fileRef.current?.click()}>
              Upload AAB
            </Button>
            {progress !== null && (
              <div className="flex min-w-[12rem] flex-1 items-center gap-2">
                <div className="h-2 flex-1 overflow-hidden rounded bg-surface-muted">
                  <div className="h-full bg-primary transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
                </div>
                <span className="text-xs text-content-muted">
                  {progress < 1 ? `${Math.round(progress * 100)}%` : 'Checking…'}
                </span>
              </div>
            )}
          </div>
        )}
        {uploadError && <p className="text-sm text-danger">{uploadError}</p>}
        {downloadError && <p className="text-sm text-danger">{downloadError}</p>}
        {builds.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-content-subtle">
                  <th className="py-1 pr-3 font-normal">Build</th>
                  <th className="py-1 pr-3 font-normal">Version</th>
                  <th className="py-1 pr-3 font-normal">Size</th>
                  <th className="py-1 pr-3 font-normal">Added</th>
                  <th className="py-1 pr-3 font-normal">Signing key SHA-256</th>
                  <th className="py-1 font-normal" />
                </tr>
              </thead>
              <tbody>
                {builds.map((b) => (
                  <tr key={b.id} className="border-t border-border">
                    <td className="py-1.5 pr-3 whitespace-nowrap">
                      <span className="text-content">{b.build_type === 'apk' ? 'APK' : 'AAB'}</span>
                      <span className="text-xs text-content-subtle"> · {b.source === 'factory' ? 'App Factory' : 'Upload'}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-content">{b.version_name} <span className="text-content-subtle">({b.version_code})</span></td>
                    <td className="py-1.5 pr-3 text-content-muted">{megabytes(b.size_bytes)}</td>
                    <td className="py-1.5 pr-3 text-content-muted whitespace-nowrap">{stamp(b.created_at)}</td>
                    <td className="py-1.5 pr-3 font-mono text-[11px] text-content-subtle break-all">{b.signer_sha256}</td>
                    <td className="py-1.5 text-right">
                      {b.build_type === 'apk' && (
                        <Button variant="secondary" size="sm" loading={downloading === b.id} disabled={!!downloading} onClick={() => void downloadApk(b)}>
                          Download APK
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !buildsQ.isLoading && (
            <p className="text-sm text-content-subtle">{factoryBuilt ? 'No build yet.' : 'No build uploaded yet.'}</p>
          )
        )}
      </div>

      {isClient && factoryBuilt && <PlayerPage appId={appId} canEdit={canEdit} />}

      {/* Submission */}
      <div className="space-y-2 border-t border-border pt-3">
        {open ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-content">{STATUS_LABEL[open.status]}</span>
            <span className="text-xs text-content-subtle">
              {open.version_name} ({open.version_code}) · {stamp(open.created_at)}
            </span>
            {isClient && open.status === 'submitted' && canEdit && (
              <Button variant="secondary" size="sm" loading={withdraw.isPending} onClick={() => withdraw.mutate(open.id)}>
                Withdraw
              </Button>
            )}
          </div>
        ) : (
          canEdit && (
            <>
              {missing.length > 0 && (
                <p className="text-sm text-content-muted">
                  Before submitting, add: <span className="text-content">{missing.join(', ')}</span>.
                </p>
              )}
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[12rem]">
                  <label className="block text-xs text-content-muted mb-1">Build</label>
                  <select value={chosenBuild} onChange={(e) => setBuildId(e.target.value)} disabled={bundles.length === 0}
                    className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content">
                    {bundles.map((b) => <option key={b.id} value={b.id}>AAB {b.version_name} ({b.version_code})</option>)}
                  </select>
                </div>
                <div className="min-w-[14rem] flex-1">
                  <label className="block text-xs text-content-muted mb-1">Comment (optional)</label>
                  <input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000}
                    className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content" />
                </div>
                <Button loading={submit.isPending} disabled={!chosenBuild || missing.length > 0} onClick={() => submit.mutate()}>
                  Submit for publishing
                </Button>
              </div>
              {submit.error && <p className="text-sm text-danger">{(submit.error as Error).message}</p>}
            </>
          )
        )}
        <SubmissionHistory submissions={submissions} staff={!isClient && isTechnical} appId={appId} onChanged={refresh} />
      </div>
    </section>
  );
}

/**
 * The page an App Factory build opens for a player: one of the client's own
 * tracker campaigns, or none.
 */
function PlayerPage({ appId, canEdit }: { appId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const offerQ = useQuery({ queryKey: ['sdk-offer', appId], queryFn: () => getSdkOffer(appId) });
  const campaignsQ = useQuery({ queryKey: ['tracker-campaigns'], queryFn: getCampaigns });
  const save = useMutation({
    mutationFn: (campaignId: string | null) => setSdkOffer(appId, campaignId),
    onSuccess: (data) => qc.setQueryData(['sdk-offer', appId], data),
  });
  const current = offerQ.data?.enabled ? (offerQ.data.campaign_id ?? '') : '';
  const campaigns = (campaignsQ.data ?? []).filter((c) => c.status === 'active' || c.id === current);

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <div>
        <h4 className="text-sm font-medium text-content">Player page</h4>
        <p className="text-xs text-content-subtle">Apps built by App Factory open this campaign for players.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={current}
          disabled={!canEdit || offerQ.isLoading || campaignsQ.isLoading || save.isPending}
          onChange={(e) => save.mutate(e.target.value || null)}
          className="min-w-[16rem] px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
        >
          <option value="">Off</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {save.isPending && <span className="text-xs text-content-muted">Saving…</span>}
      </div>
      {!campaignsQ.isLoading && campaigns.length === 0 && (
        <p className="text-xs text-content-subtle">No active tracker campaigns yet — create one in Tracker first.</p>
      )}
      {[offerQ.error, campaignsQ.error, save.error].filter(Boolean).map((e, i) => (
        <p key={i} className="text-sm text-danger">{(e as Error).message}</p>
      ))}
    </div>
  );
}

function SubmissionHistory({ submissions, staff, appId, onChanged }: {
  submissions: Submission[]; staff: boolean; appId: string; onChanged: () => void;
}) {
  const shown = useMemo(() => submissions.slice(0, staff ? 10 : 5), [submissions, staff]);
  if (shown.length === 0) return null;
  return (
    <ul className="space-y-2">
      {shown.map((s) => (
        <li key={s.id} className="rounded-md border border-border p-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-content">{STATUS_LABEL[s.status]}</span>
            <span className="text-xs text-content-subtle">
              {s.version_name} ({s.version_code}) · {stamp(s.created_at)}{s.created_by_email ? ` · ${s.created_by_email}` : ''}
            </span>
          </div>
          {s.client_comment && <p className="mt-1 text-content-muted">“{s.client_comment}”</p>}
          {s.client_note && !staff && <p className="mt-1 text-content">{s.client_note}</p>}
          {staff && <StaffControls key={`${s.id}:${s.updated_at}`} submission={s} appId={appId} onChanged={onChanged} />}
        </li>
      ))}
    </ul>
  );
}

const STAFF_STATUSES: SubmissionStatus[] = ['in_review', 'published', 'rejected'];

function StaffControls({ submission, onChanged }: { submission: Submission; appId: string; onChanged: () => void }) {
  const [status, setStatus] = useState<SubmissionStatus>(submission.status);
  const [clientNote, setClientNote] = useState(submission.client_note ?? '');
  const [staffNote, setStaffNote] = useState(submission.staff_note ?? '');
  const save = useMutation({
    mutationFn: () => updateSubmission(submission.id, {
      ...(status !== submission.status ? { status } : {}),
      client_note: clientNote, staff_note: staffNote,
    }),
    onSuccess: onChanged,
  });
  if (submission.status === 'cancelled') return null;
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-[10rem_1fr_1fr_auto] sm:items-end">
      <select value={status} onChange={(e) => setStatus(e.target.value as SubmissionStatus)}
        className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content">
        {submission.status === 'submitted' && <option value="submitted">Submitted</option>}
        {STAFF_STATUSES.map((st) => <option key={st} value={st}>{STATUS_LABEL[st]}</option>)}
      </select>
      <input value={clientNote} onChange={(e) => setClientNote(e.target.value)} placeholder="Note the client sees"
        className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content" />
      <input value={staffNote} onChange={(e) => setStaffNote(e.target.value)} placeholder="Internal note"
        className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content" />
      <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
      {save.error && <p className="text-xs text-danger sm:col-span-4">{(save.error as Error).message}</p>}
    </div>
  );
}
